import {
  CdkDragDrop,
  moveItemInArray,
  CdkDropList,
  CdkDrag,
  CdkDragHandle,
} from '@angular/cdk/drag-drop';
import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewEncapsulation,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  NomOutputFormat,
  NomOutputFormatSettings,
  NomSupportedLanguages,
} from 'src/app/core/constants';
import { Observable, Subscription, map } from 'rxjs';
import {
  OutputSettings,
  OutputSettingsFormat,
  Property,
  PropertyGroup,
} from 'src/app/core/models/output-settings.models';

import { FormsService } from 'src/app/core/services/forms.service';
import { LoggerService } from '@services/logger.service';
import { OutputSettingsService } from 'src/app/core/services/output-settings.service';
import { OutputSettingsTransformationService } from 'src/app/core/services/output-settings-transformation.service';
import { ComboboxOption, SuiComboboxComponent } from '@sui/combobox';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass, AsyncPipe } from '@angular/common';
import { NgpLabel } from 'ng-primitives/form-field';
import { SuiLabelDirective } from '@sui/form-field';
import { SuiInputComponent } from '@sui/input';
import { SuiCheckboxComponent } from '@sui/checkbox';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslatePipe } from '@ngx-translate/core';
import { ComboboxOptionsPipe } from '../../../pipes/combobox-options.pipe';
import { HighlightPipe } from '../../../pipes/highlight.pipe';

interface LayoutElement {
  id: string;
  name: string;
  selected: boolean;
  order: number;
}

@Component({
  selector: 'app-output-settings-format-editor',
  templateUrl: './output-settings-format-editor.component.html',
  styleUrls: ['./output-settings-format-editor.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    NgpLabel,
    SuiLabelDirective,
    SuiInputComponent,
    SuiComboboxComponent,
    SuiCheckboxComponent,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    FaIconComponent,
    AsyncPipe,
    TranslatePipe,
    ComboboxOptionsPipe,
    HighlightPipe,
  ],
})
export class OutputSettingsFormatEditorComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() outputSettings: OutputSettings;
  @Input() outputFormatId: NomOutputFormat;
  @Input() formGroup: FormGroup;

  // * Configuration inputs for different use cases
  @Input() showComponents: boolean = true; // *   Layout elements section
  @Input() showProperties: boolean = true; // *   Properties section
  @Input() showSettings: boolean = true; // *     Settings section
  @Input() showInternalTabs: boolean = true; // * show or hide internal Tabs -> (Content Settings / Widget Settings)
  @Input() useWidgetLayout: boolean = false; // * Widget-specific styling
  @Input() numberOfColumns: number = 3; // *      3 for Feed, 1 for Widget
  @Input() fieldsDisabled: boolean = false; // *  Disable properties and settings fields (Widget override mode)
  @Input() searchString$: Observable<string>;

  public searchString: string = '';
  public supportedLanguages = NomSupportedLanguages;
  public templateOptions$: Observable<any[]>;
  // sui-combobox options for the template picker (`{name,value}` -> `{label,value}`).
  // "No template" is not an option: the combobox shows its placeholder while the
  // control holds the empty value, and `clearable` lets the user return to it.
  public templateSelectOptions$: Observable<ComboboxOption[]>;
  public layoutElements: LayoutElement[] = [];

  public activeTab: string = 'content';
  public propertiesColumns: any[][] = [];

  private formSubscription: Subscription;
  private isDragging: boolean = false; // Flag to prevent circular sync during drag

  get currentFormat(): OutputSettingsFormat | undefined {
    return this.outputSettings?.formats?.find(
      (format) => format.id === this.outputFormatId,
    );
  }

  get formatName(): string {
    return this.currentFormat?.name || 'Unknown Format';
  }

  get isWidgetMode(): boolean {
    return this.useWidgetLayout;
  }

  get shouldShowLeftPanel(): boolean {
    return (
      !this.useWidgetLayout &&
      (this.showComponents || this.currentFormat?.has_templates)
    );
  }

  get hasMultipleFormats(): boolean {
    return this.outputSettings?.formats?.length > 1;
  }

  get widgetLayoutColumnsClass(): string {
    return this.isWidgetMode ? 'col-12' : 'col-md-8';
  }

  private logger = inject(LoggerService);
  private destroyRef = inject(DestroyRef);

  constructor(
    private outputSettingsService: OutputSettingsService,
    private formsService: FormsService,
    private outputSettingsTransformationService: OutputSettingsTransformationService,
  ) {}

  ngOnInit(): void {
    this.logger.debug('formGroup -->> ', this.formGroup);
    this.initializeComponent();
    if (this.searchString$) {
      this.searchString$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((val) => {
          this.searchString = val ?? '';
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // ! only reinitialize if critical inputs change
    if (
      changes['outputSettings'] ||
      changes['outputFormatId'] ||
      changes['formGroup']
    ) {
      if (this.outputSettings && this.outputFormatId && this.formGroup) {
        this.initializeComponent();
      }
    }

    // * Handle fieldsDisabled changes
    if (changes['fieldsDisabled'] && this.formGroup) {
      this.updateFieldsDisabledState();
    }
  }

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  private initializeComponent(): void {
    if (!this.outputSettings || !this.outputFormatId || !this.formGroup) {
      return;
    }

    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }

    this.loadTemplateOptions();
    this.initializeLayoutElementsFromFormat();
    this.updatePropertiesColumns();

    // * subscribe to Form changes to keep layout elements in sync
    this.formSubscription = this.formGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        this.syncLayoutElementsFromForm();
      });

    // * initial sync
    this.syncLayoutElementsFromForm();

    // * Apply initial disabled state
    this.updateFieldsDisabledState();
  }

  // * Update form controls disabled state based on fieldsDisabled input
  private updateFieldsDisabledState(): void {
    if (!this.formGroup) return;

    // * Disable/enable properties controls (property_*_value)
    if (this.showProperties) {
      Object.keys(this.formGroup.controls).forEach((controlName) => {
        if (controlName.startsWith('property_')) {
          const control = this.formGroup.get(controlName);
          if (control) {
            if (this.fieldsDisabled) {
              control.disable();
            } else {
              control.enable();
            }
          }
        }
      });
    }

    // * Disable/enable settings controls (setting_*_value)
    if (this.showSettings) {
      Object.keys(this.formGroup.controls).forEach((controlName) => {
        if (controlName.startsWith('setting_')) {
          const control = this.formGroup.get(controlName);
          if (control) {
            if (this.fieldsDisabled) {
              control.disable();
            } else {
              control.enable();
            }
          }
        }
      });
    }
  }

  setActiveTab(tabId: string): void {
    this.activeTab = tabId;
  }

  isActiveTab(tabId: string): boolean {
    return this.activeTab === tabId;
  }

  onLayoutElementDrop(event: CdkDragDrop<LayoutElement[]>): void {
    // ! prevent circular sync
    this.isDragging = true;

    moveItemInArray(
      this.layoutElements,
      event.previousIndex,
      event.currentIndex,
    );

    // * update order values
    this.layoutElements.forEach((element, index) => {
      element.order = index;
    });

    // * update Form with new order
    this.updateLayoutElementsInForm();

    // * re-enable sync after a short delay
    setTimeout(() => {
      this.isDragging = false;
    });
  }

  onLayoutElementToggle(elementId: string, checked: boolean): void {
    const element = this.layoutElements.find((el) => el.id === elementId);
    if (element) {
      element.selected = checked;
      this.updateLayoutElementsInForm();
    }
  }

  onLayoutElementCheckboxChange(elementId: string, checked: boolean): void {
    this.onLayoutElementToggle(elementId, checked);
  }

  private updateLayoutElementsInForm(): void {
    // * temporarily disable Form change subscription to prevent circular updates
    const shouldMarkDirty = !this.isDragging;

    this.layoutElements.forEach((element) => {
      const controlName = `layout_${element.id}_selected`;
      const selectedControl = this.formGroup.get(controlName);
      if (selectedControl && selectedControl.value !== element.selected) {
        selectedControl.setValue(element.selected, { emitEvent: false });
      }

      const orderControlName = `layout_${element.id}_order`;
      const orderControl = this.formGroup.get(orderControlName);
      if (orderControl && orderControl.value !== element.order) {
        orderControl.setValue(element.order, { emitEvent: false });
      }
    });

    if (shouldMarkDirty) {
      this.formGroup.markAsDirty();
    }

    // * manually emit the final Form change after all updates
    if (this.isDragging) {
      // * emit the Form change once after drag is complete
      // ! delay to prevent circular sync
      setTimeout(() => {
        this.formGroup.updateValueAndValidity();
      }, 100);
    }
  }

  private syncLayoutElementsFromForm(): void {
    // * skip sync during drag operations to prevent circular updates
    if (this.isDragging) {
      return;
    }

    // * sync Layout elements state from Form values
    this.layoutElements.forEach((element) => {
      const selectedControl = this.formGroup.get(
        `layout_${element.id}_selected`,
      );
      const orderControl = this.formGroup.get(`layout_${element.id}_order`);

      if (selectedControl) {
        element.selected = selectedControl.value;
      }
      if (orderControl) {
        element.order = orderControl.value;
      }
    });

    // ! re-sort -> sort by order
    this.layoutElements.sort((a, b) => a.order - b.order);
  }

  private loadTemplateOptions(): void {
    if (this.currentFormat?.has_templates) {
      this.templateOptions$ = this.outputSettingsService
        .getOutputTemplatesForFormat(this.outputFormatId)
        .pipe(
          map((templates) => {
            return this.formsService.transformForSelect(
              templates,
              (template) => template.id, //
              (template) => template.name,
              null,
            );
          }),
        );

      this.templateSelectOptions$ = this.templateOptions$.pipe(
        map((options) =>
          options.map((o) => ({ label: o.name, value: o.value })),
        ),
      );
    }
  }

  private initializeLayoutElementsFromFormat(): void {
    const format = this.currentFormat;
    if (!format) return;

    // * initialize Layout elements from Format Components (if available)
    if (format.components && format.components.length > 0) {
      const componentsCopy = [...format.components];
      const sortedComponents = componentsCopy.sort((a, b) => a.index - b.index);

      this.layoutElements = sortedComponents.map((component, index) => ({
        id: this.getLayoutElementId(component.name),
        name: component.name,
        selected: component.selected,
        order: index,
      }));
    } else {
      // * default Layout elements
      this.layoutElements = [
        {
          id: 'table_of_contents',
          name: 'Table of Contents',
          selected: true,
          order: 0,
        },
        { id: 'widgets', name: 'Widgets', selected: true, order: 1 },
        { id: 'content', name: 'Content', selected: true, order: 2 },
      ];
    }
  }

  private getLayoutElementId(componentName: string): string {
    // * map Component names to our Layout element IDs
    return this.outputSettingsTransformationService.getLayoutElementId(
      componentName,
    );
  }

  matches(text: string): boolean {
    if (!this.searchString) return true;
    return text.toLowerCase().includes(this.searchString.toLowerCase());
  }

  groupHasMatches(group: PropertyGroup): boolean {
    if (!this.searchString) return true;
    return group.properties.some((p) => this.matches(p.title));
  }

  public getFormValue(): any {
    return this.formGroup.value;
  }

  /**
   * sort in that w
   * A  D  G
     B  E  H
     C  F  I
   */

  private updatePropertiesColumns(): void {
    const properties: Property[] = this.currentFormat?.properties || [];

    // Group properties by group_key
    const groupedMap = properties.reduce(
      (acc, prop) => {
        const groupKey = prop.group_key ?? 'default';
        if (!acc[groupKey]) {
          acc[groupKey] = {
            label: prop.group?.label ?? 'Default',
            order: prop.group?.order ?? 0,
            key: groupKey,
            properties: [],
          };
        }
        acc[groupKey].properties.push(prop);
        return acc;
      },
      {} as Record<string, PropertyGroup>,
    );

    // Sort groups by 'order' and properties within groups by 'title'
    const sortedGroups = Object.values(groupedMap).sort(
      (a, b) => a.order - b.order,
    );
    sortedGroups.forEach((group) => {
      group.properties.sort(
        (a, b) => (a?.property_order ?? 0) - (b?.property_order ?? 0),
      );
    });

    // Distribute groups into columns
    const numColumns = this.numberOfColumns;
    const columns: PropertyGroup[][] = Array.from(
      { length: numColumns },
      () => [],
    );

    sortedGroups.forEach((group, index) => {
      const columnIndex = index % numColumns; // Distributes horizontally across columns
      columns[columnIndex].push(group);
    });

    this.propertiesColumns = columns;
  }

  // Helper method for the "Select All" checkbox
  toggleGroup(group: PropertyGroup, isChecked: boolean): void {
    group.properties.forEach((prop) => {
      const control = this.formGroup.get('property_' + prop.key + '_value');
      if (control && !this.fieldsDisabled) {
        control.setValue(isChecked);
      }
    });
  }

  // WAP-1515: the "Second header language" list is meaningful only while the
  // bilingual-headers toggle in the same settings group is on.
  isSettingGated(groupId: number, settingId: number): boolean {
    let gated = false;
    if (settingId === NomOutputFormatSettings.DOCX_BILINGUAL_HEADERS_LANGUAGE) {
      const toggleControl = this.formGroup.get(
        `setting_${groupId}_${NomOutputFormatSettings.DOCX_BILINGUAL_HEADERS}_value`,
      );
      gated = toggleControl ? toggleControl.value !== true : false;
    }
    return gated;
  }

  // Inside your component class
  togglePropertyControl(property: Property): void {
    const isSelected = this.formGroup.get(
      `property_${property.key}_value`,
    )?.value;
    const valueControl = this.formGroup.get(
      `property_${property.key}_property_value`,
    );

    if (valueControl) {
      if (isSelected && !this.fieldsDisabled) {
        valueControl.enable();
      } else {
        valueControl.disable();
        //valueControl.setValue(property.values ? [] : false); // Clear value when disabled
      }
    }

    // Enable/disable per-property option controls alongside the parent toggle
    property.options?.forEach((option) => {
      const optionControl = this.formGroup.get(
        `property_${property.key}_option_${option.key}`,
      );
      if (optionControl) {
        if (isSelected && !this.fieldsDisabled) {
          optionControl.enable();
        } else {
          optionControl.disable();
        }
      }
    });
  }
}
